/**
 * Animation Manager
 *
 * Manages THREE.js animations for objects (characters, props, etc.)
 * Handles animation clips, transitions, and state management.
 * Supports Unity-style animation tree transitions where animations
 * can automatically transition to another state when finished.
 *
 * @example
 * ```typescript
 * const animationManager = new AnimationManager(characterMesh, {
 *     clips: characterMesh.animations,
 *     defaultFadeDuration: 0.3
 * });
 *
 * // Register animations with automatic transitions
 * animationManager.registerAnimation('idle', { name: 'Idle', loop: THREE.LoopRepeat });
 * animationManager.registerAnimation('walk', { name: 'Walk', loop: THREE.LoopRepeat, timeScale: 1.2 });
 * animationManager.registerAnimation('jump', {
 *     name: 'Jump',
 *     loop: THREE.LoopOnce,
 *     nextState: 'idle'  // Automatically transition to idle when jump finishes
 * });
 *
 * // Switch animations
 * animationManager.playAnimation('walk', 0.2);
 *
 * // Update in game loop
 * animationManager.update(deltaTime);
 * ```
 */

import * as THREE from 'three';
import type { Object3D, AnimationClip, AnimationAction, AnimationMixer } from 'three';
import type { AnimationManagerConfig, AnimationStateConfig, AnimationState } from '../types';

export class AnimationManager {
    private target: Object3D;
    private mixer: AnimationMixer | null = null;
    private animations: Map<string, AnimationState> = new Map();
    private currentState: string | null = null;
    private config: Required<AnimationManagerConfig>;
    private clips: AnimationClip[] = [];
    private onFinishedCallback: ((stateName: string) => void) | null = null;

    constructor(target: Object3D, config: AnimationManagerConfig = {}) {
        this.target = target;

        this.config = {
            clips: config.clips || [],
            defaultFadeDuration: config.defaultFadeDuration ?? 0.3,
            caseInsensitive: config.caseInsensitive ?? true,
            nameMappings: config.nameMappings || {}
        };

        // Try to get animations from target if not provided
        if (this.config.clips.length === 0 && (this.target as any).animations) {
            this.config.clips = (this.target as any).animations as AnimationClip[];
        }

        this.clips = this.config.clips;

        // Initialize mixer if we have animations
        if (this.clips.length > 0) {
            this.mixer = new THREE.AnimationMixer(this.target);

            // Listen for animation finished events
            this.mixer.addEventListener('finished', (event: any) => {
                this.handleAnimationFinished(event);
            });
        }
    }

    /**
     * Handle animation finished event - trigger automatic transitions
     */
    private handleAnimationFinished(event: any): void {
        const finishedAction = event.action as AnimationAction;

        // Find which animation state this action belongs to
        for (const [stateName, state] of this.animations) {
            if (state.action === finishedAction) {
                if (this.onFinishedCallback) this.onFinishedCallback(stateName);
                if (state.config.nextState && this.currentState === stateName) {
                    const nextState = state.config.nextState;
                    const transitionDuration = state.config.transitionDuration ?? this.config.defaultFadeDuration;
                    if (this.hasAnimation(nextState)) this.playAnimation(nextState, transitionDuration, { cutCurrent: false });
                }
                break;
            }
        }
    }

    /**
     * Register an animation state
     */
    registerAnimation(name: string, config: AnimationStateConfig): boolean {
        if (!this.mixer) {
            console.warn(`[AnimationManager] No mixer available. Cannot register animation: ${name}`);
            return false;
        }

        // Find animation clip
        let clip: AnimationClip | null = null;

        if (typeof config.name === 'string') {
            // Find by name
            clip = this.findAnimationClip(config.name);
        } else {
            // Direct clip reference
            clip = config.name;
        }

        if (!clip) {
            console.warn(`[AnimationManager] Animation clip not found: ${config.name}`);
            return false;
        }

        // Create action
        const action = this.mixer.clipAction(clip);

        // Configure action
        const loopMode = (config.loop ?? THREE.LoopRepeat) as THREE.AnimationActionLoopStyles;
        // For LoopOnce, repetitions should be 1 to fire the 'finished' event properly
        // For LoopRepeat/LoopPingPong, use Infinity
        const repetitions = loopMode === THREE.LoopOnce ? 1 : Infinity;
        action.setLoop(loopMode, repetitions);
        action.setEffectiveWeight(config.weight ?? 1.0);
        action.timeScale = config.timeScale ?? 1.0;
        action.clampWhenFinished = config.clampWhenFinished ?? true;

        // Store state
        this.animations.set(name, {
            name,
            action,
            config
        });

        return true;
    }

    /**
     * Play an animation with optional fade transition.
     * @param options.cutCurrent - If true, cut current animation instantly (no fade out) then fade in the new one. Avoids mixing when transitioning from a finished clip (e.g. jump -> idle).
     */
    playAnimation(name: string, fadeDuration?: number, options?: { cutCurrent?: boolean }): boolean {
        if (this.currentState === name) {
            return true; // Already playing
        }

        const state = this.animations.get(name);
        if (!state || !state.action) {
            console.warn(`[AnimationManager] Animation not found: ${name}`);
            return false;
        }

        const fadeInDuration = fadeDuration ?? this.config.defaultFadeDuration;
        const cutCurrent = options?.cutCurrent ?? false;

        // Fade out or cut current animation (cut = instant, no mixing with next)
        if (this.currentState) {
            const currentState = this.animations.get(this.currentState);
            if (currentState?.action) {
                if (cutCurrent) {
                    currentState.action.stop();
                } else {
                    // Even if the action finished (LoopOnce + clampWhenFinished),
                    // keep it enabled so we can fade it out smoothly.
                    currentState.action.enabled = true;
                    currentState.action.fadeOut(fadeInDuration);
                }
            }
        }

        // Fade in new animation
        state.action.reset().fadeIn(fadeInDuration).play();
        this.currentState = name;

        return true;
    }

    /**
     * Stop current animation
     */
    stopAnimation(fadeDuration?: number): void {
        if (this.currentState) {
            const state = this.animations.get(this.currentState);
            if (state?.action) {
                const duration = fadeDuration ?? this.config.defaultFadeDuration;
                if (state.action.isRunning()) {
                    state.action.fadeOut(duration);
                } else {
                    state.action.stop();
                }
            }
            this.currentState = null;
        }
    }

    /**
     * Get current animation state name
     */
    getCurrentState(): string | null {
        return this.currentState;
    }

    /**
     * Check if an animation is registered
     */
    hasAnimation(name: string): boolean {
        return this.animations.has(name);
    }

    /**
     * Update animation mixer (call in game loop)
     */
    update(deltaTime: number): void {
        if (this.mixer) {
            this.mixer.update(deltaTime);
        }
    }

    /**
     * Find animation clip by name using regex pattern matching (case-insensitive by default)
     */
    private findAnimationClip(name: string): AnimationClip | null {
        // Check name mappings first
        const mappedName = this.config.nameMappings[name] || name;

        if (this.config.caseInsensitive) {
            // Use regex to match the pattern anywhere in the animation name
            // Escape special regex characters and create case-insensitive pattern
            const escapedName = mappedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const pattern = new RegExp(escapedName, 'i');

            return this.clips.find(clip =>
                pattern.test(clip.name)
            ) || null;
        }

        // Exact match if case-sensitive
        return this.clips.find(clip => clip.name === mappedName) || null;
    }
}
